import React, { useCallback, useEffect, useRef, useState } from 'react';

/*
  Site-wide feedback widget.

  Deliberately one textarea and one button. Every field added to a feedback
  form costs completions, and on a casual games site the thing worth optimising
  is the number of people who say anything at all — not how well-structured
  what they say is. Which game they were playing is captured automatically from
  the route rather than asked.

  Submission never blocks on the network being there: if the endpoint is not
  configured or the request fails, the note is queued in localStorage and
  retried on the next page load, so feedback typed on a flaky mobile connection
  is not silently thrown away.
*/

const ENDPOINT = process.env.REACT_APP_FEEDBACK_URL || '';
const QUEUE_KEY = 'vedicgame.feedback.queue';
const MAX_LENGTH = 2000;

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(-25)));
  } catch (err) {
    /* storage full or blocked — dropping the queue is acceptable */
  }
}

async function postFeedback(payload) {
  if (!ENDPOINT) throw new Error('no endpoint configured');
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`http ${res.status}`);
  return res.json().catch(() => ({}));
}

// Which game the player is looking at, derived from the URL so we never have
// to ask and never have to thread props through the app.
function currentGame() {
  const m = window.location.pathname.match(/\/games\/([\w-]+)/);
  if (m) return m[1];
  if (window.location.pathname.startsWith('/kurukshetra/')) return 'kurukshetra-unity';
  return 'site';
}

/*
  Gold on deep navy, matching the site header. The launcher sits bottom-left:
  bottom-right is where every game in this collection puts its own HUD, and a
  floating button over a player's score is a bug report waiting to happen.
*/
const CSS = `
.vg-feedback-fab {
  position: fixed; left: 18px; bottom: 18px; z-index: 9998;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 15px; border-radius: 999px; cursor: pointer;
  font-family: inherit; font-size: 13px; font-weight: 600; letter-spacing: .04em;
  color: #f5c518; background: rgba(16, 16, 38, .88);
  border: 1px solid rgba(245, 197, 24, .38);
  box-shadow: 0 6px 22px rgba(0, 0, 0, .42);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  transition: transform .16s ease, border-color .16s ease, background .16s ease;
}
.vg-feedback-fab:hover {
  transform: translateY(-2px);
  border-color: rgba(245, 197, 24, .8);
  background: rgba(24, 24, 52, .95);
}
.vg-feedback-fab:focus-visible { outline: 2px solid #f5c518; outline-offset: 3px; }

.vg-feedback-panel {
  position: fixed; left: 18px; bottom: 66px; z-index: 9999; width: 340px;
  max-width: calc(100vw - 36px);
  background: #12122c; color: #f0e6d3;
  border: 1px solid rgba(245, 197, 24, .34); border-radius: 12px;
  box-shadow: 0 18px 52px rgba(0, 0, 0, .6);
  padding: 18px; font-family: inherit;
  animation: vgFeedbackIn .17s ease-out;
}
@keyframes vgFeedbackIn {
  from { opacity: 0; transform: translateY(8px) scale(.98); }
  to { opacity: 1; transform: none; }
}
.vg-feedback-panel h3 {
  margin: 0 0 6px; font-size: 16px; color: #f5c518; letter-spacing: .02em;
}
.vg-feedback-sub { margin: 0 0 11px; font-size: 12.5px; line-height: 1.55; opacity: .74; }
.vg-feedback-panel textarea {
  width: 100%; box-sizing: border-box; resize: vertical; min-height: 92px;
  padding: 10px 11px; font: inherit; font-size: 13.5px; line-height: 1.5;
  color: #f0e6d3; background: rgba(0, 0, 0, .3);
  border: 1px solid rgba(245, 197, 24, .22); border-radius: 7px;
}
.vg-feedback-panel textarea:focus {
  outline: none; border-color: rgba(245, 197, 24, .68);
  background: rgba(0, 0, 0, .42);
}
.vg-feedback-panel textarea::placeholder { color: rgba(240, 230, 211, .38); }
.vg-feedback-actions {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-top: 11px;
}
.vg-feedback-hint { font-size: 11px; opacity: .5; letter-spacing: .03em; }
.vg-feedback-panel button[type="submit"] {
  font: inherit; font-size: 13px; font-weight: 700; letter-spacing: .06em;
  padding: 9px 20px; border: none; border-radius: 6px; cursor: pointer;
  color: #16162e; background: linear-gradient(180deg, #f5c518, #d4a12a);
}
.vg-feedback-panel button[type="submit"]:disabled {
  opacity: .38; cursor: default; background: #6d6d7e; color: #16162e;
}
.vg-feedback-done { text-align: center; padding: 12px 4px 6px; }
.vg-feedback-done strong { display: block; font-size: 17px; color: #f5c518; margin-bottom: 5px; }
.vg-feedback-done p { margin: 0; font-size: 12.5px; opacity: .72; line-height: 1.55; }

/* On phones the launcher collapses to an icon so it never covers controls. */
@media (max-width: 560px) {
  .vg-feedback-fab span { display: none; }
  .vg-feedback-fab { padding: 10px; left: 12px; bottom: 12px; }
  .vg-feedback-panel { left: 12px; right: 12px; width: auto; bottom: 60px; }
}

@media (prefers-reduced-motion: reduce) {
  .vg-feedback-fab, .vg-feedback-panel { transition: none; animation: none; }
}
`;

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | queued
  const textareaRef = useRef(null);
  const panelRef = useRef(null);

  // Drain anything stranded by an earlier failure, once, quietly.
  useEffect(() => {
    const queue = readQueue();
    if (queue.length === 0 || !ENDPOINT) return;
    let cancelled = false;
    (async () => {
      const remaining = [];
      for (const item of queue) {
        try {
          await postFeedback(item);
        } catch (err) {
          remaining.push(item);
        }
      }
      if (!cancelled) writeQueue(remaining);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  // Escape closes, and a click outside closes — but only when idle, so a
  // stray click cannot discard something half-typed after a failed send.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || status === 'sending') return;

    const payload = {
      message: text.slice(0, MAX_LENGTH),
      game: currentGame(),
      url: window.location.href,
      referrer: document.referrer || '',
      userAgent: navigator.userAgent,
      screen: `${window.innerWidth}x${window.innerHeight}`,
      at: new Date().toISOString(),
    };

    setStatus('sending');
    try {
      await postFeedback(payload);
      setStatus('sent');
    } catch (err) {
      writeQueue([...readQueue(), payload]);
      setStatus('queued');
    }
    setMessage('');
    if (window.vedicAnalytics) {
      window.vedicAnalytics.gameEvent(payload.game, payload.game, 'feedback_submitted', {});
    }
    setTimeout(() => { setOpen(false); setStatus('idle'); }, 2200);
  }, [message, status]);

  return (
    <>
      {/* Styles live with the component, matching how App.js scopes its own. */}
      <style>{CSS}</style>
      <button
        type="button"
        className="vg-feedback-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Help me improve these games"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
          <path
            d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.9 9.9 0 0 1-2.9-.4L3 21l1.6-4.7A8.2 8.2 0 0 1 3 11.5a8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 9 8.4z"
            fill="none" stroke="currentColor" strokeWidth="1.9"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
        <span>Help me improve</span>
      </button>

      {open && (
        <div className="vg-feedback-panel" ref={panelRef} role="dialog" aria-label="Feedback">
          {status === 'sent' || status === 'queued' ? (
            <div className="vg-feedback-done">
              <strong>Thank you.</strong>
              <p>
                {status === 'sent'
                  ? 'Read personally, usually the same day.'
                  : "Saved — it'll send next time you're online."}
              </p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h3>Help me improve these games</h3>
              <p className="vg-feedback-sub">
                What would make this better? Anything at all — what was confusing,
                what was boring, what broke, what you wish existed.
              </p>
              <textarea
                ref={textareaRef}
                value={message}
                maxLength={MAX_LENGTH}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me what you really think…"
                rows={5}
                onKeyDown={(e) => {
                  // Cmd/Ctrl+Enter sends, because people expect it to.
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(e);
                }}
              />
              <div className="vg-feedback-actions">
                <span className="vg-feedback-hint">
                  {message.trim() ? `${message.trim().length}/${MAX_LENGTH}` : 'Goes straight to the developer'}
                </span>
                <button type="submit" disabled={!message.trim() || status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
